import type { RewardRow } from "@/modules/loyalty/domain/types";
import { confirm } from "@/shared/lib/confirm";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { DataTable, createSortableColumn, TanstackReactTable } from "@/shared/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";



import { EditIcon, PowerOffIcon } from "lucide-react";

type RewardsTableProps = {
  data: RewardRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onEdit: (row: RewardRow) => void;
  onDeactivate: (id: string) => void | Promise<void>;
};

export function RewardsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onSortingChange,
  onPaginationChange,
  onEdit,
  onDeactivate,
}: RewardsTableProps) {
  const columns: TanstackReactTable.ColumnDef<RewardRow>[] = [
    {
      id: "image",
      header: "ຮູບ",
      size: 72,
      cell: ({ row }) => {
        const src = resolveImageSrc(row.original.imageUrl);
        return src ? (
          <img
            src={src}
            alt=""
            className="size-12 rounded-md border object-cover"
          />
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        );
      },
    },
    createSortableColumn<RewardRow>("name", "ຊື່ລາງວັນ", { size: 220 }),
    createSortableColumn<RewardRow>("requiredPoints", "ແຕ້ມທີ່ໃຊ້ແລກ", {
      size: 120,
      cell: ({ row }) =>
        `${row.original.requiredPoints.toLocaleString("lo-LA")} ແຕ້ມ`,
    }),
    {
      accessorKey: "variantDisplayName",
      header: "ສິນຄ້າໃຫ້ເປັນລາງວັນ",
      size: 240,
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.original.variantDisplayName ?? "– ບໍ່ກຳນົດ –"}
        </span>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "isActive",
      header: "ສະຖານະ",
      size: 100,
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? "default" : "secondary"}>
          {row.original.isActive ? "ເປີດໃຊ້ງານ" : "ປິດໃຊ້ງານ"}
        </Badge>
      ),
      enableSorting: false,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(row.original)}
            title="ແກ້ໄຂ"
          >
            <EditIcon className="size-4" />
            ແກ້ໄຂ
          </Button>
          {row.original.isActive && (
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                const ok = await confirm({
                  title: "ປິດໃຊ້ງານລາງວັນ",
                  description: `ລາງວັນ "${row.original.name}" ຈະບໍ່ສາມາດແລກໄດ້ອີກ. ຕ້ອງການດຳເນີນການບໍ?`,
                  actionText: "ປິດໃຊ້ງານ",
                  cancelText: "ຍົກເລີກ",
                });
                if (ok) onDeactivate(row.original.id);
              }}
              title="ປິດໃຊ້ງານ"
            >
              <PowerOffIcon className="size-4" />
              ປິດໃຊ້ງານ
            </Button>
          )}
        </div>
      ),
      size: 220,
      enableSorting: false,
    },
  ];

  return (
    <DataTable<RewardRow, unknown>
      noDataMessage="ບໍ່ພົບລາງວັນ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
